import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import PdfViewerContainer from "../../../components/PdfViewerContainer";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";

const SELECT = "SELECT";
const FORM_NAME = "SBI_CA1";
const MODIFY_ENDPOINT = "/backend/api/ajax_modify.php";
const PDF_ENDPOINT = "/ModifyMyForm/SBI_CA1/CurrentPrint.php";

const entityFields = [
  { name: "acno", label: "Account Number", maxLength: 11, numeric: true },
  {
    name: "enttype",
    label: "Entity Constitution Type",
    type: "select",
    required: true,
    options: [
      ["PROPRIETOR", "Sole Proprietorship"],
      ["PFIRM", "Partnership Firm"],
      ["HUF", "HUF"],
      ["PRIVATELTD", "Private Limited Company"],
      ["PUBLICLTD", "Public Limited Company"],
      ["SOCIETY", "Society"],
      ["AOP", "Association of Persons / Body of Individuals"],
      ["TRUST", "Trust"],
      ["GOVT", "Government Department / Agency"],
      ["OTHERS", "Others"],
    ],
  },
  { name: "entname", label: "Entity Name", maxLength: 74, alphaOnly: true, required: true },
  { name: "entdoi", label: "Date of Incorporation / Formation", type: "date", required: true },
  { name: "entpan", label: "PAN Number (Entity)", maxLength: 10, uppercase: true },
  { name: "gstn", label: "GST Number", maxLength: 15, uppercase: true },
  {
    name: "poi",
    label: "Proof of Identity",
    type: "select",
    required: true,
    options: [
      ["COI", "Certificate of Incorporation / Formation"],
      ["OVD", "Officially Valid Document of Authorized Person"],
      ["PDEED", "MOA/AOA, Partnership Deed or Trust Document"],
      ["RC", "Registration Certificate"],
      ["ROB", "Board / Managing Committee Resolution"],
      ["AP", "Activity Proof (Sole Proprietorship only)"],
      ["OTHER", "Other"],
    ],
  },
  { name: "norp", label: "Number of Related Persons", type: "select", required: true, options: ["1", "2"] },
  {
    name: "nob",
    label: "Nature of Business",
    type: "select",
    required: true,
    options: [
      ["MANUFACTURER", "Manufacturer"],
      ["TRADER", "Trader"],
      ["RETAILER", "Retailer"],
      ["SP", "Service Provider"],
      ["EXPORT", "Export / Import"],
      ["OTHERS", "Others"],
    ],
  },
  {
    name: "turnover",
    label: "Turnover",
    type: "select",
    required: true,
    options: [
      ["0_5", "0 - 5 Lakh"],
      ["5_10", "5 - 10 Lakh"],
      ["10_25", "10 - 25 Lakh"],
      ["25_1CR", "25 Lakh - 1 Crore"],
      ["1CR_5CR", "1 Crore - 5 Crore"],
      ["5CR_50CR", "5 Crore - 50 Crore"],
      ["50CR_100CR", "50 Crore - 100 Crore"],
      ["100CR+", "Above 100 Crore"],
    ],
  },
];

const entityAddressFields = [
  { name: "entadd1", label: "Address Line 1", maxLength: 40, required: true },
  { name: "entadd2", label: "Address Line 2", maxLength: 40, required: true },
  { name: "entadd3", label: "Address Line 3", maxLength: 22, required: true },
  { name: "entcity", label: "City", maxLength: 13, required: true },
  { name: "entdist", label: "District", maxLength: 23, required: true },
  { name: "entstate", label: "State", maxLength: 13, required: true },
  { name: "entpin", label: "PIN", maxLength: 6, required: true, numeric: true },
  { name: "entmobile", label: "Mobile", maxLength: 10, required: true, numeric: true },
  { name: "entemail", label: "Email", type: "email", maxLength: 37, required: false },
];

const personalOptions = {
  gender: ["MALE", "FEMALE"],
  marital: ["MARRIED", "UNMARRIED"],
};

const occupationOptions = [
  "SERVICE",
  ["PUBLIC_SECTOR", "Public Sector"],
  ["PRIVATE_SECTOR", "Private Sector"],
  ["GOVERNMET_SECTOR", "Government Sector"],
  "PROFESSIONAL",
  ["SELF_EMPLOYED", "Self Employed"],
  "RETIRED",
  "HOUSEWIFE",
  "STUDENT",
  "BUSINESS",
  ["NOT_CATEGORIZED", "Not Categorised"],
  "OTHERS",
];

function applicantPersonalFields(prefix) {
  return [
    { name: `${prefix}name`, label: "Applicant Name", maxLength: 32, alphaOnly: true, required: true },
    { name: `${prefix}dob`, label: "Date of Birth", type: "date", required: true },
    { name: `${prefix}gender`, label: "Gender", type: "select", required: true, options: personalOptions.gender },
    { name: `${prefix}mstatus`, label: "Marital Status", type: "select", required: true, options: personalOptions.marital },
    { name: `${prefix}aadhaarno`, label: "Aadhaar Number", maxLength: 12, required: true, numeric: true },
    { name: `${prefix}pan`, label: "PAN Number", maxLength: 10, uppercase: true },
    { name: `${prefix}fname`, label: "Father's Name", maxLength: 32, alphaOnly: true, required: true },
    { name: `${prefix}sname`, label: "Spouse Name", maxLength: 32, alphaOnly: true, required: true, marriedOnly: true },
    { name: `${prefix}mname`, label: "Mother's Name", maxLength: 32, alphaOnly: true, required: true },
  ];
}

function applicantAddressFields(prefix) {
  return [
    { name: `${prefix}add1`, label: "Address Line 1", maxLength: 37, required: true },
    { name: `${prefix}add2`, label: "Address Line 2", maxLength: 37, required: true },
    { name: `${prefix}add3`, label: "Address Line 3", maxLength: 22, required: true },
    { name: `${prefix}city`, label: "City", maxLength: 13, required: true },
    { name: `${prefix}dist`, label: "District", maxLength: 23, required: true },
    { name: `${prefix}state`, label: "State", maxLength: 13, required: true },
    { name: `${prefix}pin`, label: "PIN", maxLength: 6, required: true, numeric: true },
    { name: `${prefix}bplace`, label: "Birth Place", maxLength: 13, required: true },
    { name: `${prefix}mobile`, label: "Mobile", maxLength: 10, required: true, numeric: true },
    { name: `${prefix}occupation`, label: "Occupation", type: "select", required: true, options: occupationOptions },
  ];
}

const firstPersonalFields = applicantPersonalFields("fa");
const firstAddressFields = applicantAddressFields("fa");
const secondPersonalFields = applicantPersonalFields("sa");
const secondAddressFields = applicantAddressFields("sa");

const nominationFields = [
  { name: "isnomination", label: "Is Nomination?", type: "select", required: true, options: ["YES", "NO"] },
  { name: "nname", label: "Nominee Name", maxLength: 36, alphaOnly: true, required: true, nominationOnly: true },
  {
    name: "nrelation",
    label: "Relation",
    type: "select",
    required: true,
    nominationOnly: true,
    options: ["FATHER", "MOTHER", "HUSBAND", "WIFE", "SON", "DAUGHTER", "BROTHER", "SISTER", "OTHER"],
  },
  { name: "ndob", label: "Date of Birth", type: "date", required: true, nominationOnly: true },
  { name: "ngname", label: "Guardian's Name", maxLength: 36, alphaOnly: true, required: true, minorNomineeOnly: true },
  { name: "ngage", label: "Guardian's Age", maxLength: 2, required: true, numeric: true, minorNomineeOnly: true },
];

const allFields = [
  ...entityFields,
  ...entityAddressFields,
  ...firstPersonalFields,
  ...firstAddressFields,
  ...secondPersonalFields,
  ...secondAddressFields,
  ...nominationFields,
];

const initialFormData = allFields.reduce((result, field) => {
  result[field.name] = field.type === "select" ? SELECT : "";
  return result;
}, {});

const tabs = [
  "Entity",
  "Entity Address",
  "First Person",
  "First Address",
  "Second Person",
  "Second Address",
  "Nomination",
  "Preview",
];

const tabTitles = [
  "🏢 Entity Details",
  "🏠 Entity Address Details",
  "👤 First Related Person - Personal Details",
  "🏠 First Related Person - Address Details",
  "👤 Second Related Person - Personal Details",
  "🏠 Second Related Person - Address Details",
  "👥 Nomination Details",
];

function optionValue(option) {
  return Array.isArray(option) ? option[0] : option;
}

function optionLabel(option) {
  return Array.isArray(option) ? option[1] : option;
}

function ageFromDate(value) {
  if (!value) return 0;
  const dob = new Date(value);
  if (Number.isNaN(dob.getTime())) return 0;
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const month = today.getMonth() - dob.getMonth();
  if (month < 0 || (month === 0 && today.getDate() < dob.getDate())) age -= 1;
  return age;
}

function formatDate(value) {
  if (!value) return "";
  const [year, month, day] = value.split("-");
  return year && month && day ? `${day}/${month}/${year}` : value;
}

function validateField(field, value) {
  const normalizedValue = String(value ?? "").trim();

  if (field.required) {
    if (field.type === "select" && (!normalizedValue || normalizedValue === SELECT)) {
      return `${field.label} is required.`;
    }

    if (!normalizedValue) {
      return `${field.label} is required.`;
    }
  }

  if (!normalizedValue || normalizedValue === SELECT) return "";

  if (field.alphaOnly && !/^[A-Za-z ]+$/.test(normalizedValue)) {
    return `${field.label} must contain alphabet letters only.`;
  }

  if (field.name.includes("aadhaar") && !/^\d{12}$/.test(normalizedValue)) {
    return "Aadhaar number must contain exactly 12 digits.";
  }

  if (field.name.endsWith("mobile") && !/^\d{10}$/.test(normalizedValue)) {
    return "Mobile number must contain 10 digits.";
  }

  if (field.name.endsWith("pin") && !/^\d{6}$/.test(normalizedValue)) {
    return "PIN must contain exactly 6 digits.";
  }

  if (field.name.includes("pan") && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(normalizedValue)) {
    return "Enter a valid PAN number (e.g. ABCDE1234F).";
  }

  return "";
}

export default function Current() {
  const location = useLocation();
  const navigate = useNavigate();
  const refno = location.state?.refno || new URLSearchParams(location.search).get("refno") || "";
  const [currentTab, setCurrentTab] = useState(0);
  const [maxAllowedTab, setMaxAllowedTab] = useState(0);
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [pdfUrl, setPdfUrl] = useState("");
  const [showPdf, setShowPdf] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState("");
  const [recordLoading, setRecordLoading] = useState(true);
  const nextButtonRef = useRef(null);

  const hasSecondPerson = formData.norp === "2";
  const isProprietor = formData.enttype === "PROPRIETOR";
  const hasNomination = isProprietor && formData.isnomination === "YES";
  const nomineeIsMinor = formData.ndob ? ageFromDate(formData.ndob) < 18 : true;

  const visibleTabIndexes = useMemo(() => {
    const indexes = [0, 1, 2, 3];
    if (hasSecondPerson) indexes.push(4, 5);
    if (isProprietor) indexes.push(6);
    indexes.push(7);
    return indexes;
  }, [hasSecondPerson, isProprietor]);

  const fieldsForTab = useMemo(() => {
    const firstPersonal = firstPersonalFields.filter(
      (field) => !field.marriedOnly || formData.famstatus === "MARRIED",
    );
    const secondPersonal = secondPersonalFields.filter(
      (field) => !field.marriedOnly || formData.samstatus === "MARRIED",
    );
    const nomination = nominationFields.filter((field) => {
      if (field.name === "isnomination") return true;
      if (!hasNomination) return false;
      if (field.minorNomineeOnly) return nomineeIsMinor;
      return true;
    });

    return {
      0: entityFields,
      1: entityAddressFields,
      2: firstPersonal,
      3: firstAddressFields,
      4: secondPersonal,
      5: secondAddressFields,
      6: nomination,
      7: [],
    };
  }, [formData.famstatus, formData.samstatus, hasNomination, nomineeIsMinor]);

  const visibleFields = fieldsForTab[currentTab] || [];
  const firstFieldName = visibleFields[0]?.name || "";
  const lastFieldName = visibleFields[visibleFields.length - 1]?.name || "";

  useEffect(() => {
    if (!firstFieldName) return undefined;
    const frame = window.requestAnimationFrame(() => {
      document.getElementById(firstFieldName)?.focus();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [currentTab, firstFieldName]);

  useEffect(() => {
    const controller = new AbortController();

    async function loadSavedRecord() {
      if (!refno) {
        setPdfError("Reference number is missing. Please open this form from the saved-forms list.");
        setRecordLoading(false);
        return;
      }
      try {
        const requestBody = new FormData();
        requestBody.append("action", "get");
        requestBody.append("form_name", FORM_NAME);
        requestBody.append("refno", refno);

        const response = await fetch(MODIFY_ENDPOINT, {
          method: "POST",
          credentials: "include",
          cache: "no-store",
          headers: { Accept: "application/json" },
          body: requestBody,
          signal: controller.signal,
        });

        const responseText = await response.text();
        let result;

        try {
          result = responseText ? JSON.parse(responseText) : {};
        } catch {
          throw new Error("The server returned an invalid response.");
        }

        const savedForm = result.data || result.form;

        if (!response.ok || !result.success || !savedForm) {
          throw new Error(result.message || "Saved form could not be loaded.");
        }

        setFormData((previous) => {
          const next = { ...previous };
          allFields.forEach((field) => {
            const savedValue = savedForm[field.name];

            if (savedValue !== undefined && savedValue !== null) {
              next[field.name] =
                field.type === "select" && String(savedValue).trim() === ""
                  ? SELECT
                  : String(savedValue);
            }
          });
          return next;
        });
      } catch (error) {
        if (error.name !== "AbortError") setPdfError(error.message || "Unable to load the saved form.");
      } finally {
        if (!controller.signal.aborted) setRecordLoading(false);
      }
    }
    loadSavedRecord();
    return () => controller.abort();
  }, [refno]);

  useEffect(() => () => {
    if (pdfUrl?.startsWith("blob:")) URL.revokeObjectURL(pdfUrl);
  }, [pdfUrl]);

  const updateValue = (name, rawValue) => {
    const field = allFields.find((item) => item.name === name);
    let value = rawValue;
    if (field?.numeric) value = value.replace(/\D/g, "");
    if (field?.alphaOnly) value = value.replace(/[^A-Za-z ]/g, "");
    if (field?.uppercase || field?.name.includes("pan")) value = value.toUpperCase();

    setFormData((previous) => {
      const next = { ...previous, [name]: value };

      if (name === "enttype" && value !== "PROPRIETOR") {
        next.isnomination = "NO";
        next.nname = "";
        next.nrelation = SELECT;
        next.ndob = "";
        next.ngname = "";
        next.ngage = "";
      }
      if (name === "norp" && value === "1") {
        secondPersonalFields.concat(secondAddressFields).forEach((item) => {
          next[item.name] = item.type === "select" ? SELECT : "";
        });
      }
      if (name === "famstatus" && value !== "MARRIED") next.fasname = "";
      if (name === "samstatus" && value !== "MARRIED") next.sasname = "";
      if (name === "isnomination" && value !== "YES") {
        next.nname = "";
        next.nrelation = SELECT;
        next.ndob = "";
        next.ngname = "";
        next.ngage = "";
      }
      if (name === "ndob" && ageFromDate(value) >= 18) {
        next.ngname = "";
        next.ngage = "";
      }
      return next;
    });

    setErrors((previous) => ({ ...previous, [name]: "" }));
  };

  const handleLastFieldKeyDown = (event, fieldName) => {
    if (fieldName === lastFieldName && event.key === "Tab" && !event.shiftKey) {
      event.preventDefault();
      nextButtonRef.current?.focus();
    }
  };

  const validateCurrentTab = () => {
    const nextErrors = {};
    visibleFields.forEach((field) => {
      const message = validateField(field, formData[field.name]);
      if (message) nextErrors[field.name] = message;
    });
    setErrors((previous) => ({ ...previous, ...nextErrors }));
    return Object.keys(nextErrors).length === 0;
  };

  const copyFirstAddressToSecond = () => {
    setFormData((previous) => ({
      ...previous,
      sacity: previous.facity,
      sadist: previous.fadist,
      sastate: previous.fastate,
      sapin: previous.fapin,
    }));
  };

  const goNext = (event) => {
    event?.preventDefault();
    event?.stopPropagation();
    if (!validateCurrentTab()) return;

    if (currentTab === 3 && hasSecondPerson) copyFirstAddressToSecond();

    const position = visibleTabIndexes.indexOf(currentTab);
    const nextTab = visibleTabIndexes[position + 1];
    if (nextTab !== undefined) {
      setCurrentTab(nextTab);
      setMaxAllowedTab((previous) => Math.max(previous, nextTab));
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const goPrevious = () => {
    const position = visibleTabIndexes.indexOf(currentTab);
    const previousTab = visibleTabIndexes[position - 1];
    if (previousTab !== undefined) {
      setCurrentTab(previousTab);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setPdfLoading(true);
    setPdfError("");

    try {
      const body = new FormData();
      Object.entries(formData).forEach(([name, value]) => {
        body.append(name, value === SELECT ? "" : String(value ?? ""));
      });
      body.append("action", "update_and_print");
      body.append("form_name", FORM_NAME);
      body.append("refno", refno);

      const response = await fetch(PDF_ENDPOINT, {
        method: "POST",
        body,
        credentials: "include",
        headers: { Accept: "application/pdf" },
      });

      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || `PDF generation failed (${response.status}).`);
      }

      const contentType = response.headers.get("content-type") || "";
      if (!contentType.includes("application/pdf")) {
        const message = await response.text();
        throw new Error(message || "CurrentPrint.php did not return a PDF file.");
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      setPdfUrl((previous) => {
        if (previous?.startsWith("blob:")) URL.revokeObjectURL(previous);
        return objectUrl;
      });
      setShowPdf(true);
    } catch (error) {
      console.error("Current account PDF error:", error);
      setPdfError(error.message || "Unable to generate PDF preview.");
    } finally {
      setPdfLoading(false);
    }
  };

  const renderField = (field) => {
    const error = errors[field.name];
    return (
      <div key={field.name} className="space-y-2">
        <Label htmlFor={field.name}>
          {field.label}
          {field.required && <span className="text-red-500"> *</span>}
        </Label>

        {field.type === "select" ? (
          <Select value={formData[field.name]} onValueChange={(value) => updateValue(field.name, value)}>
            <SelectTrigger
              id={field.name}
              className={error ? "border-red-500" : ""}
              onKeyDown={(event) => handleLastFieldKeyDown(event, field.name)}
            >
              <SelectValue placeholder={`Select ${field.label}`} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={SELECT}>SELECT</SelectItem>
              {field.options.map((option) => (
                <SelectItem key={optionValue(option)} value={optionValue(option)}>
                  {optionLabel(option)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Input
            id={field.name}
            name={field.name}
            type={field.type || "text"}
            value={formData[field.name]}
            maxLength={field.maxLength}
            placeholder={field.label}
            className={error ? "border-red-500" : ""}
            onChange={(event) => updateValue(field.name, event.target.value)}
            onKeyDown={(event) => handleLastFieldKeyDown(event, field.name)}
          />
        )}
        {error && <p className="text-sm text-red-500">{error}</p>}
      </div>
    );
  };

  const previewSections = [
    ["Entity Details", entityFields],
    ["Entity Address", entityAddressFields],
    ["First Related Person", firstPersonalFields],
    ["First Related Person Address", firstAddressFields],
    ...(hasSecondPerson
      ? [["Second Related Person", secondPersonalFields], ["Second Related Person Address", secondAddressFields]]
      : []),
    ...(isProprietor ? [["Nomination", fieldsForTab[6]]] : []),
  ];

  if (showPdf && pdfUrl) {
    return <PdfViewerContainer pdfUrl={pdfUrl} title="SBI Current Account Opening Form" onBack={() => setShowPdf(false)} />;
  }

  if (recordLoading) {
    return <div className="flex min-h-[60vh] items-center justify-center text-lg">Loading saved SBI current-account form...</div>;
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="mx-auto max-w-7xl">
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-3xl">🏦 SBI Current Account Opening Form</CardTitle>
            <p className="text-muted-foreground">Modify saved form: <strong>{refno || "Reference unavailable"}</strong></p>
          </CardHeader>
        </Card>

        <div className="mb-8 grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-8">
          {visibleTabIndexes.map((tabIndex) => (
            <Button
              key={tabs[tabIndex]}
              type="button"
              variant={currentTab === tabIndex ? "default" : "outline"}
              disabled={tabIndex > maxAllowedTab}
              onClick={() => setCurrentTab(tabIndex)}
            >
              {visibleTabIndexes.indexOf(tabIndex) + 1}. {tabs[tabIndex]}
            </Button>
          ))}
        </div>

        {currentTab !== 7 ? (
          <Card>
            <CardHeader>
              <CardTitle>{tabTitles[currentTab]}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {visibleFields.map(renderField)}
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>📋 Preview</CardTitle>
            </CardHeader>
            <CardContent className="space-y-8">
              {previewSections.map(([title, fields]) => (
                <section key={title}>
                  <h3 className="mb-3 text-lg font-semibold">{title}</h3>
                  <div className="overflow-hidden rounded-lg border">
                    {fields.map((field) => (
                      <div key={field.name} className="grid grid-cols-2 border-b p-3 last:border-b-0">
                        <span className="font-medium">{field.label}</span>
                        <span className="break-words">
                          {field.type === "date"
                            ? formatDate(formData[field.name])
                            : field.type === "select"
                              ? optionLabel(field.options?.find((option) => optionValue(option) === formData[field.name]) || formData[field.name])
                              : formData[field.name] || "-"}
                        </span>
                      </div>
                    ))}
                  </div>
                </section>
              ))}
            </CardContent>
          </Card>
        )}

        {pdfError && <p className="mt-4 rounded-lg border border-red-300 bg-red-50 p-3 text-red-700">{pdfError}</p>}

        <div className="mt-6 flex items-center justify-between">
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => navigate("/ModifyMyForm/SBI_CA1/FormsListCurrent")}>← Saved Forms</Button>
            <Button type="button" variant="outline" onClick={goPrevious} disabled={currentTab === visibleTabIndexes[0]}>Previous</Button>
          </div>

          {currentTab === 7 ? (
            <Button type="button" ref={nextButtonRef} onClick={handleSubmit} disabled={pdfLoading}>
              {pdfLoading ? "Updating & generating PDF..." : "Update & Preview PDF"}
            </Button>
          ) : (
            <Button type="button" ref={nextButtonRef} onClick={goNext}>
              {visibleTabIndexes[visibleTabIndexes.indexOf(currentTab) + 1] === 7 ? "Preview" : "Next →"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
